import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createCustomBuddy, createDigitalPrintCheckout, createColoringPageCheckout } from "../lib/api";
import PrintOptionsModal from "./PrintOptionsModal";
import ShareBuddyButton from "./ShareBuddyButton";
import { useCart } from "../lib/cart";
import { useAccess } from "../lib/access";
import { trackEvent } from "../lib/analytics";
import {
  Wand2,
  Loader2,
  CalendarPlus,
  Clock,
  Utensils,
  Sparkles,
  Heart,
  Baby,
  Trash2,
  ShoppingBag,
  FileDown,
  Mail,
  Lock,
  Palette,
} from "lucide-react";
import { toast } from "sonner";

const PROMPT_IDEAS = [
  "A grumpy bowl of ramen with a tiny chef hat and big sleepy eyes",
  "A taco knight with a salsa shield and avocado sword",
  "A dancing donut with rainbow sprinkles and roller skates",
];

const STORAGE_KEY = "platebuddies_custom_buddies_v1";

function loadLibrary() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeLibrary(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* noop */
  }
}

export default function CreateYourOwn() {
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [digitalLoading, setDigitalLoading] = useState(false);
  const [coloringLoading, setColoringLoading] = useState(false);
  const [printPickerBuddy, setPrintPickerBuddy] = useState(null);
  const [result, setResult] = useState(null);
  const [library, setLibrary] = useState(loadLibrary);
  const [savedId, setSavedId] = useState(null);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { access } = useAccess();

  const refreshLibrary = (next) => {
    setLibrary(next);
    writeLibrary(next);
  };

  const saveToLibrary = (buddy) => {
    if (!buddy) return null;
    const filtered = library.filter((b) => b.id !== buddy.id);
    const next = [buddy, ...filtered].slice(0, 24);
    refreshLibrary(next);
    setSavedId(buddy.id);
    return buddy.id;
  };

  const removeFromLibrary = (id) => {
    refreshLibrary(library.filter((b) => b.id !== id));
    if (savedId === id) setSavedId(null);
    toast.success("Removed from your library.");
  };

  const applyIdea = (idea) => setPrompt(idea);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Give your Buddy a name first!");
      return;
    }
    if (!prompt.trim()) {
      toast.error("Describe your Buddy or pick a starter idea.");
      return;
    }
    setLoading(true);
    setResult(null);
    setSavedId(null);
    try {
      const data = await createCustomBuddy({ name, prompt, email: access?.email });
      setResult(data);
      trackEvent("funnel_buddy_generated", { food_theme: data.food_theme, source: "create_buddy" });
      toast.success(`${data.name} is ready! Preview below.`);
    } catch (err) {
      // Surface the rate-limit message verbatim — it's user-friendly copy.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error("Couldn't generate your Buddy. Try again in a sec.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToLibrary = () => {
    if (!result) return;
    saveToLibrary(result);
    toast.success(
      <span className="flex items-center gap-2">
        <span>
          <Heart className="inline h-4 w-4 fill-pb-coral text-pb-coral mr-1" />
          {result.name} saved!
        </span>
        <button
          onClick={() => navigate("/library")}
          className="rounded-full bg-pb-ink text-white text-xs font-extrabold px-3 py-1.5 hover:bg-pb-coral transition"
          data-testid="toast-view-library"
        >
          View in My Library →
        </button>
      </span>,
      { duration: 6000 }
    );
  };

  const goToPlanner = () => {
    if (!result) return;
    saveToLibrary(result);
    toast.success(`${result.name} is on the magnet tray — ready to plan!`);
    navigate("/planner");
  };

  const planFromLibrary = (buddy) => {
    saveToLibrary(buddy);
    navigate("/planner");
  };

  const orderPrint = (buddy) => {
    saveToLibrary(buddy);
    addItem({
      product_id: "buddy_print",
      name: `Custom Buddy Print — ${buddy.name}`,
      unit_price: 14.99,
      custom_buddy_id: buddy.id,
      custom_buddy_name: buddy.name,
      custom_buddy_image: buddy.image,
    });
    toast.success(`${buddy.name} print added to cart!`);
    navigate("/cart");
  };

  const addDigitalToCart = (buddy) => {
    const target = buddy || result;
    if (!target) return;
    saveToLibrary(target);
    addItem({
      product_id: "buddy_print_digital",
      name: `Digital Buddy Print Bundle — ${target.name}`,
      unit_price: 5.00,
      custom_buddy_id: target.id,
      custom_buddy_name: target.name,
      custom_buddy_image: target.image,
    });
    toast.success(`${target.name}'s digital bundle added to cart!`);
    navigate("/cart");
  };

  const addColoringToCart = (buddy) => {
    const target = buddy || result;
    if (!target) return;
    saveToLibrary(target);
    addItem({
      product_id: "buddy_coloring_page",
      name: `Coloring Page — ${target.name}`,
      unit_price: 3.00,
      custom_buddy_id: target.id,
      custom_buddy_name: target.name,
      custom_buddy_image: target.image,
    });
    toast.success(`${target.name}'s coloring page added to cart!`);
    navigate("/cart");
  };

  const orderColoringPage = async (buddy) => {
    const target = buddy || result;
    if (!target) return;
    const email = (access?.email || "").trim();
    if (!email) {
      toast.error("Add your email above to receive the coloring page.");
      return;
    }
    setColoringLoading(true);
    saveToLibrary(target);
    trackEvent("coloring_page_checkout_click", {
      buddy_id: target.id,
      food_theme: target.food_theme,
    });
    try {
      const data = await createColoringPageCheckout({
        custom_buddy_id: target.id,
        email,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't start checkout. Please try again.");
    } finally {
      setColoringLoading(false);
    }
  };

  const orderDigitalPrint = async (buddy) => {
    const target = buddy || result;
    if (!target) return;
    const email = (access?.email || "").trim();
    if (!email) {
      toast.error("Add your email above to receive the PDF.");
      return;
    }
    setDigitalLoading(true);
    // Save the buddy locally first so /library still has them after redirect.
    saveToLibrary(target);
    trackEvent("digital_print_checkout_click", {
      buddy_id: target.id,
      food_theme: target.food_theme,
    });
    try {
      const data = await createDigitalPrintCheckout({
        custom_buddy_id: target.id,
        email,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't start checkout. Please try again.");
    } finally {
      setDigitalLoading(false);
    }
  };

  return (
    <section id="create" data-testid="section-create" className="relative py-16 md:py-20 bg-white overflow-hidden scroll-mt-24">
      <div className="blob bg-pb-coral" style={{ width: 320, height: 320, top: 40, right: -120, opacity: 0.25 }} />
      <div className="blob bg-pb-sun" style={{ width: 320, height: 320, bottom: -80, left: -80, opacity: 0.4 }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">Create Your Own Buddy</p>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight">
            Who joins the <span className="wavy">family</span>?
          </h2>
          <p className="mt-5 text-pb-soft text-lg max-w-lg">
            Describe your Buddy in your own words — we illustrate it in seconds and pair it with a kid-friendly recipe ready for your meal plan.
          </p>

          <form data-testid="form-buddy" onSubmit={onSubmit} className="mt-8 space-y-5 max-w-xl">
            <Field label="Buddy name">
              <input
                data-testid="input-buddy-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Mango Mango"
                className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-5 py-3.5 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition"
                maxLength={40}
              />
            </Field>

            <Field label="Describe your Buddy">
              <textarea
                data-testid="input-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. A grumpy bowl of ramen with a tiny chef hat and big sleepy eyes…"
                rows={5}
                className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-5 py-3.5 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition resize-none leading-snug"
                maxLength={600}
              />
            </Field>

            <div>
              <p className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">Need a spark? Try one:</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {PROMPT_IDEAS.map((idea, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`prompt-idea-${i}`}
                    onClick={() => applyIdea(idea)}
                    className="text-xs font-bold text-pb-ink bg-pb-cream hover:bg-pb-sun border border-pb-sky/40 hover:border-pb-coral rounded-full px-3 py-1.5 transition"
                  >
                    {idea.length > 60 ? idea.slice(0, 58) + "…" : idea}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="btn-generate-buddy"
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-8 py-4 shadow-[0_12px_30px_rgba(242,106,52,0.4)] hover:scale-105 transition disabled:opacity-60 disabled:hover:scale-100"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />}
              {loading ? "Sketching your buddy…" : "Generate my Buddy"}
            </button>
            <p className="text-xs text-pb-soft">
              Each Buddy is illustrated in ~15–30 seconds and comes with its own coordinated recipe.
            </p>
          </form>
        </div>

        <div className="relative lg:sticky lg:top-24">
          <div className="aspect-square rounded-[3rem] bg-gradient-to-br from-pb-sky via-pb-cream to-pb-sun border-[8px] border-white shadow-[0_30px_80px_rgba(28,22,40,0.15)] p-6 flex items-center justify-center relative overflow-hidden">
            {loading && (
              <div className="text-center">
                <Loader2 className="h-12 w-12 text-pb-coral animate-spin mx-auto" />
                <p className="mt-4 font-bold text-pb-ink">Creating your Plate Buddy…</p>
                <p className="text-sm text-pb-soft mt-1">Painting the character + crafting the recipe</p>
              </div>
            )}
            {!loading && !result && (
              <div data-testid="placeholder-buddy" className="text-center px-6">
                <Sparkles className="h-16 w-16 text-pb-coral mx-auto" />
                <p className="mt-4 font-display font-black text-2xl">Your Buddy appears here</p>
                <p className="text-pb-soft mt-2">Fill in the form and hit generate.</p>
              </div>
            )}
            {!loading && result && (
              <div data-testid="result-buddy" className="text-center w-full">
                <div className="bg-white rounded-3xl p-4 inline-block shadow-[0_20px_50px_rgba(28,22,40,0.18)] rotate-[-2deg] no-print">
                  <div
                    className="relative rounded-2xl overflow-hidden bg-white buddy-preview-image"
                    onContextMenu={(e) => {
                      e.preventDefault();
                      toast.info("Print or save by ordering your Buddy — just $3, $5, or $14.99 ✨");
                    }}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <img
                      src={result.image}
                      alt={result.name}
                      draggable={false}
                      className="w-full max-w-sm h-auto select-none pointer-events-none"
                    />
                    {/* Diagonal preview watermark — visible on screen + screenshots */}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
                      <div className="rotate-[-22deg] text-[10vw] sm:text-[64px] font-extrabold uppercase tracking-[0.18em] text-pb-ink/[0.12] whitespace-nowrap select-none">
                        Preview · platebuddies
                      </div>
                    </div>
                  </div>
                  <p className="font-display font-black text-2xl mt-3">{result.name}</p>
                  {result.tagline && (
                    <p className="text-xs font-extrabold uppercase tracking-widest mt-1" style={{ color: result.color }}>
                      {result.tagline}
                    </p>
                  )}
                </div>
                <div className="mt-4 flex justify-center">
                  <ShareBuddyButton buddy={result} variant="pill" />
                </div>
                <p className="mt-3 text-[11px] text-pb-soft inline-flex items-center justify-center gap-1.5">
                  <Lock className="h-3 w-3" /> Watermark removed when you order — $3, $5, or $14.99
                </p>
              </div>
            )}
          </div>

          {!loading && result?.recipe && (
            <div
              data-testid="custom-recipe-card"
              className="mt-6 rounded-[2rem] p-6 border border-white shadow-[0_18px_40px_rgba(28,22,40,0.08)]"
              style={{ background: result.bg || "#FFF6D6" }}
            >
              <div className="flex items-center gap-3">
                <span className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white shadow-sm" style={{ color: result.color }}>
                  <Utensils className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: result.color }}>
                    Tonight&apos;s coordinated recipe
                  </p>
                  <h4 className="font-display font-black text-xl leading-tight">{result.recipe.title}</h4>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <Pill icon={Clock} label={result.recipe.time} />
                <Pill icon={Baby} label={result.recipe.kid_age} />
                <Pill icon={Sparkles} label={result.recipe.vibe} />
              </div>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-ink/70">Ingredients</p>
                  <ul className="mt-2 text-sm space-y-1">
                    {result.recipe.ingredients.slice(0, 5).map((ing, i) => (
                      <li key={i} className="text-pb-ink">• {ing}</li>
                    ))}
                    {result.recipe.ingredients.length > 5 && (
                      <li className="text-pb-soft">+ {result.recipe.ingredients.length - 5} more…</li>
                    )}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-ink/70">Steps</p>
                  <ol className="mt-2 text-sm space-y-1 list-decimal list-inside marker:text-pb-coral marker:font-bold">
                    {result.recipe.steps.slice(0, 3).map((s, i) => (
                      <li key={i} className="text-pb-ink">{s}</li>
                    ))}
                    {result.recipe.steps.length > 3 && (
                      <li className="list-none text-pb-soft">+ {result.recipe.steps.length - 3} more steps in the planner</li>
                    )}
                  </ol>
                </div>
              </div>

              {result.recipe.kid_task && (
                <div className="mt-5 bg-white/70 backdrop-blur rounded-2xl p-4 text-sm flex gap-2 items-start">
                  <Heart className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: result.color }} />
                  <span className="text-pb-ink"><strong>Kid&apos;s job: </strong>{result.recipe.kid_task}</span>
                </div>
              )}
            </div>
          )}

          {!loading && result && (
            <div
              data-testid="result-conversion-block"
              className="mt-6 space-y-3"
            >
              {/* PRIMARY CTA — $3 coloring page (lowest barrier, highest conversion) */}
              <button
                data-testid="btn-cta-coloring"
                onClick={() => orderColoringPage(result)}
                disabled={coloringLoading}
                className="w-full group relative overflow-hidden rounded-[1.5rem] bg-pb-coral text-white p-5 hover:scale-[1.01] transition shadow-[0_18px_40px_rgba(242,106,52,0.4)] disabled:opacity-60 disabled:hover:scale-100 text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 shadow-inner">
                    {coloringLoading ? (
                      <Loader2 className="h-7 w-7 animate-spin" />
                    ) : (
                      <Palette className="h-7 w-7" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">
                      Color in {result.name}
                    </p>
                    <p className="font-display font-black text-xl sm:text-2xl leading-tight mt-0.5">
                      {coloringLoading ? "Opening checkout…" : "Send my coloring page"}
                    </p>
                    <p className="text-sm text-white/90 mt-1 font-bold">
                      Instant printable PDF · Just $3
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-display font-black text-4xl leading-none">$3</p>
                  </div>
                </div>
              </button>

              <p className="text-center text-[10px] uppercase tracking-widest text-pb-soft font-extrabold">
                — or upgrade —
              </p>

              {/* SECONDARY: $5 Buddy + Recipe bundle */}
              <button
                data-testid="btn-cta-digital"
                onClick={() => orderDigitalPrint(result)}
                disabled={digitalLoading}
                className="w-full group rounded-[1.25rem] bg-white border-2 border-pb-sky/40 hover:border-pb-coral p-4 transition disabled:opacity-60 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-pb-cream flex items-center justify-center flex-shrink-0">
                    {digitalLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-pb-coral" />
                    ) : (
                      <FileDown className="h-5 w-5 text-pb-coral" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-black text-pb-ink text-base sm:text-lg leading-tight">
                      Buddy + recipe bundle
                    </p>
                    <p className="text-xs text-pb-soft mt-0.5">
                      Full-color print + matching recipe card
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <span className="font-display font-black text-xl text-pb-ink">$5</span>
                    <span className="text-pb-coral font-extrabold group-hover:translate-x-1 transition">→</span>
                  </div>
                </div>
              </button>

              {/* TERTIARY: $14.99 laminated set */}
              <button
                data-testid="btn-cta-physical"
                onClick={() => orderPrint(result)}
                className="w-full group rounded-[1.25rem] bg-white border-2 border-pb-sky/40 hover:border-pb-ink p-4 transition text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-pb-cream flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="h-5 w-5 text-pb-ink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-black text-pb-ink text-base sm:text-lg leading-tight">
                      Laminated mat + magnet
                    </p>
                    <p className="text-xs text-pb-soft mt-0.5">
                      Shipped free in the US · The real deal
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <span className="font-display font-black text-xl text-pb-ink">$14.99</span>
                    <span className="text-pb-ink font-extrabold group-hover:translate-x-1 transition">→</span>
                  </div>
                </div>
              </button>

              {/* Reassurance line */}
              <p className="text-center text-[11px] text-pb-soft pt-1 leading-relaxed">
                <Lock className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                Sized for a 7&quot; plate · No account needed · Watermark removed instantly
              </p>
            </div>
          )}

          {/* Tertiary actions — small, low-emphasis, parked at the bottom */}
          {!loading && result && (
            <div className="mt-6 flex items-center justify-center gap-4 flex-wrap text-[11px] text-pb-soft">
              {savedId === result.id ? (
                <span
                  data-testid="badge-saved"
                  className="inline-flex items-center gap-1.5 text-pb-green font-extrabold"
                >
                  <Heart className="h-3 w-3 fill-current" /> Saved
                </span>
              ) : (
                <button
                  data-testid="btn-save-library"
                  onClick={handleAddToLibrary}
                  className="inline-flex items-center gap-1.5 font-extrabold hover:text-pb-coral underline underline-offset-2 transition"
                >
                  <Heart className="h-3 w-3" /> Save for later
                </button>
              )}
              <span className="text-pb-sky/60">·</span>
              <button
                data-testid="btn-add-to-planner"
                onClick={goToPlanner}
                className="inline-flex items-center gap-1.5 font-extrabold hover:text-pb-coral underline underline-offset-2 transition"
              >
                <CalendarPlus className="h-3 w-3" /> Add to planner
              </button>
              <span className="text-pb-sky/60">·</span>
              <a
                href="https://www.instagram.com/plate_buddies"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="link-result-instagram"
                className="inline-flex items-center gap-1.5 font-extrabold hover:text-pb-coral underline underline-offset-2 transition"
              >
                Share @plate_buddies
              </a>
            </div>
          )}
        </div>
      </div>

      {/* My Library */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">My Buddy Library</p>
            <h3 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight">
              {library.length === 0 ? "Your library is empty (for now)" : `${library.length} Buddy${library.length === 1 ? "" : " buddies"} ready to plate`}
            </h3>
          </div>
          {library.length > 0 && (
            <Link
              to="/planner"
              data-testid="link-library-to-planner"
              className="inline-flex items-center gap-2 text-sm font-extrabold text-pb-coral hover:underline"
            >
              Take them to the planner →
            </Link>
          )}
        </div>

        {library.length === 0 ? (
          <div
            data-testid="library-empty"
            className="mt-6 rounded-3xl border-2 border-dashed border-pb-sky/60 p-10 text-center bg-pb-cream"
          >
            <Sparkles className="h-10 w-10 text-pb-coral mx-auto" />
            <p className="font-display font-black text-xl mt-3">No Buddies saved yet</p>
            <p className="text-pb-soft mt-1">
              Generate a Buddy above and tap <strong>Add to my library</strong> to keep it forever.
            </p>
          </div>
        ) : (
          <div data-testid="library-grid" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {library.map((b) => (
              <div
                key={b.id}
                data-testid={`library-card-${b.id}`}
                className="rounded-[1.75rem] p-5 border border-white shadow-[0_14px_32px_rgba(28,22,40,0.08)] flex gap-4"
                style={{ background: b.bg || "#FFF6D6" }}
              >
                <div className="h-24 w-24 rounded-2xl overflow-hidden bg-white flex-shrink-0 border-4 border-white shadow-md">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-black text-lg leading-tight truncate">{b.name}</p>
                  {b.tagline && (
                    <p className="text-[10px] font-extrabold uppercase tracking-widest mt-0.5 truncate" style={{ color: b.color }}>
                      {b.tagline}
                    </p>
                  )}
                  {b.recipe?.title && (
                    <p className="text-xs text-pb-ink/70 mt-1 truncate">
                      <Utensils className="h-3 w-3 inline mr-1" />{b.recipe.title}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap items-center">
                    <button
                      data-testid={`btn-library-print-${b.id}`}
                      onClick={() => setPrintPickerBuddy(b)}
                      className="text-xs inline-flex items-center gap-1.5 rounded-full bg-pb-coral text-white font-extrabold px-4 py-2 hover:scale-105 transition shadow-[0_6px_14px_rgba(242,106,52,0.35)]"
                    >
                      <FileDown className="h-3.5 w-3.5" /> Print
                    </button>
                    <button
                      data-testid={`btn-library-plan-${b.id}`}
                      onClick={() => planFromLibrary(b)}
                      className="text-xs inline-flex items-center gap-1 rounded-full bg-white text-pb-ink font-extrabold px-3 py-1.5 hover:text-pb-coral border-2 border-pb-sky/40 hover:border-pb-coral transition"
                    >
                      <CalendarPlus className="h-3 w-3" /> Plan
                    </button>
                    <button
                      data-testid={`btn-library-delete-${b.id}`}
                      onClick={() => removeFromLibrary(b.id)}
                      className="text-xs inline-flex items-center gap-1 rounded-full bg-white/80 text-pb-soft font-bold px-2.5 py-1.5 hover:text-pb-coral hover:bg-white transition border border-pb-sky/30 ml-auto"
                      aria-label="Remove buddy"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <PrintOptionsModal
        open={!!printPickerBuddy}
        buddy={printPickerBuddy}
        loadingDigital={digitalLoading}
        loadingColoring={coloringLoading}
        onClose={() => setPrintPickerBuddy(null)}
        onPickDigital={(b) => {
          setPrintPickerBuddy(null);
          orderDigitalPrint(b);
        }}
        onPickColoring={(b) => {
          setPrintPickerBuddy(null);
          orderColoringPage(b);
        }}
        onPickPhysical={(b) => {
          setPrintPickerBuddy(null);
          orderPrint(b);
        }}
        onAddDigital={(b) => {
          setPrintPickerBuddy(null);
          addDigitalToCart(b);
        }}
        onAddColoring={(b) => {
          setPrintPickerBuddy(null);
          addColoringToCart(b);
        }}
      />
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Pill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1 text-pb-ink font-bold shadow-sm border border-white">
      <Icon className="h-3 w-3 text-pb-coral" />
      {label}
    </span>
  );
}
